// RunHistory.js — Drawer listing past simulation runs.
// Click to reload results. Shows timestamp, key params, summary return.

import React, { useEffect } from 'react';
import { useSimulator } from '../../context/SimulatorContext';
import { History, ChevronRight } from 'lucide-react';

function formatCurrency(value, ccy = 'USD') {
  if (value == null) return '—';
  const sign = value < 0 ? '-' : '+';
  return `${sign}${ccy} ${Math.abs(value).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

export default function RunHistory() {
  const { runHistory, loadHistory, loadRun, activeRun } = useSimulator();

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  if (!runHistory || runHistory.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <History className="w-8 h-8 mx-auto mb-2 opacity-30" />
        <p className="text-xs">No past simulations</p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 px-1 mb-2">
        <History className="w-4 h-4 text-gray-400" />
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Recent Runs</span>
      </div>

      {runHistory.map((run) => {
        const isActive = activeRun?.id === run.id;
        const isPositive = (run.net_return_base || run.netReturnBase || 0) > 0;

        return (
          <button
            key={run.id}
            onClick={() => loadRun(run.id)}
            className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all ${
              isActive
                ? 'bg-gray-100 border-gray-300'
                : 'bg-white border-gray-100 hover:border-gray-200 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-700">
                    {run.source_currency || run.sourceCurrency || 'USD'} {(run.investment_amount || run.investmentAmount || 0).toLocaleString()}
                  </span>
                  <span className={`text-xs font-mono ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(run.net_return_base || run.netReturnBase, run.source_currency || run.sourceCurrency)}
                  </span>
                </div>
                <div className="text-[10px] text-gray-400 mt-0.5">
                  {run.tenor_days || run.tenorDays}d | {run.start_date || run.startDate} to {run.end_date || run.endDate}
                </div>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
            </div>
          </button>
        );
      })}
    </div>
  );
}
